/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { Container, Icon, Text }  from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import VBars from "../../../CommonUi/Loaders/VBars";
import YoutubePreview from "../../../CommonUi/VideoThumbnail/YoutubePreview";
import youtube_link from "../../../../assets/youtube_link.svg";
import youtube_file from "../../../../assets/youtube_file.svg";
import RateFeedback from "components/CommonUi/RateFeedback/RateFeedback";
import EditDescriptionModal from "./EditDescriptionModal";
import MediaPreview from "components/CommonUi/MediaPreview/MediaPreview";

interface YoutubeUrlUploadProps {
  isLoading: boolean;
  url: string | null;
  setUrl: (url: string) => void;
  textToUpload: string;
  videoUrlType: "youtube" | "invalid" | null;
  error: string | null;
  clearData: () => void;
  handleUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTextChange: (val: string) => void;
  CHARACTER_LIMIT: number;
  t: (key: string) => string;
  inputWrapper: any;
  videoUrlRef: React.MutableRefObject<string | null>;
  asset: any;
}

const YoutubeUrlUpload: React.FC<YoutubeUrlUploadProps> = ({
  isLoading,
  url,
  setUrl,
  textToUpload,
  videoUrlType,
  error,
  clearData,
  handleUrlChange,
  handleTextChange,
  CHARACTER_LIMIT,
  t,
  inputWrapper,
  videoUrlRef,
  asset,
}) => {
  const theme = useTheme();
  const [isEditDescOpen, setIsEditDescOpen] = useState<boolean>(false);
  const [characterCount, setCharacterCount] = useState<number>(
    textToUpload?.length || 0
    );

    const loaderWrapperStyles = css`
    padding: 12px 0;
    `;
    const fileUploadStyles = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: 1px solid ${theme.colors.strokeGray};
    padding: ${theme.spacing.xl};
    text-align: center;
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};

    &:hover {
    cursor: pointer;
    border-color: ${theme.colors.primary};
    background-color: ${theme.colors.background};
    }          
    `;

    const youtubeUrlWrapper = css`
      border: 1px solid ${theme.colors.strokeGray};
      border-radius: ${theme.radius.sm};
      padding: 16px;
    `;

    const containerStyle = css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid ${theme.colors.strokeGray};
    padding: 0 ${theme.spacing.lg};
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};
    background-color: ${theme.colors.background};
    box-sizing: border-box;
    margin-bottom: 8px;
    `;

    const fileNameContentStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing.md};
    width: 100%;

    @media (max-width: 450px) {
    flex-direction: column;
    }
    `;

    const replaceFileStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-radius: ${theme.radius.sm};
    border: 1px solid ${theme.colors.background};

    &:hover {
    cursor: pointer;
    border-color: ${theme.colors.primary};
    }

    @media (max-width: 450px) {
    flex-direction: column;
    }

    .itemWrapper{
      color: ${theme.colors.primary};
    }
    `;

    const replaceTextStyles = css`
      color: ${theme.colors.primary};
      font-weight: 600;
      line-height: 150%;
      text-align: center;
    `;
    const prefixStyles = css`
    color: #777;
    font-size: 14px;
    margin-right: 4px;
    white-space: nowrap;
    padding-left: 10px;
    `
    const counterStyles = css`
    font-size: ${theme.typography.bodyMedium.fontSize};
    color: ${textToUpload.length >= CHARACTER_LIMIT
    ? theme.colors.red
    : theme.colors.textMedium};
    text-align: right;
    `;
    const inputStyle = css`
    width: 100%;
    `;

    const titleSection = css`
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      margin: 20px 0 8px;
    `;
  
    const editBttn = css`
      cursor: pointer;
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 2px;
      color: ${theme.colors.primary};
  
      &>div{
        margin-top: -6px;
      }
    `;

    const textareaStyles = css`
        color: ${theme.colors.textMedium};
        height: 110px;
        text-align: justify;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: auto;
        text-overflow: ellipsis;
        width: 100%;
        font-size: 16px;
        padding: 10px;
        box-sizing: border-box;
        border-radius: ${theme.radius.xs};
        border: 1px solid hsl(198, 11%, 71%);
        
        /* Scrollbar Styling */
        &::-webkit-scrollbar {
          width: 10px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: hsl(198, 11%, 60%);
          border-radius: 10px;
        }

        &::-webkit-scrollbar-button{
          display: none;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: hsl(198, 11%, 45%);
        }

        /* 🎯 Hide or recolor scrollbar buttons (top/down arrows) */
        &::-webkit-scrollbar-button {
          background: white; /* or transparent to hide completely */
          width: 0;
          height: 0;
          display: none;
        }

        /* Firefox */
        scrollbar-color: hsl(198, 11%, 60%) transparent;
      `;
  return (
    <div css={css`gap: 0;`}>
      {isLoading && (
        <Container variant="column" css={fileUploadStyles}>
          <div css={loaderWrapperStyles}>
            <VBars size="medium" />
            <Text weight="semibold">{t("analyse_fileInput_waiting")}</Text>
            <div>{t("analyse_fileInput_waiting_subText")}</div>
          </div>
        </Container>
      )}

      {!isLoading && (
        <>
          {/* URL Input Section */}
          {!textToUpload && (
            <>
              <div style={{ textAlign: "left" }}>
                {t("form_inputVideoUrlLabel")}
              </div>
              <div css={inputWrapper}>
                <div css={css`display: flex; align-items: center; padding-left: 8px;`}>
                  <img src={youtube_link} alt="YouTube" style={{ marginRight: "4px" }} />
                </div>
                <input
                  name="url"
                  placeholder="https://"
                  onChange={(ev) => setUrl(ev.target.value)}
                  autoComplete="off"
                  value={url || ""}
                  css={inputStyle}
                  onBlur={handleUrlChange}
                />
              </div>
            </>
          )}
          {textToUpload && (
            <div css={youtubeUrlWrapper}>
              {/* Video Preview Section */}
              {videoUrlType === "youtube" && url && !error && (
                <>
                  <Container css={containerStyle}>
                    <div css={fileNameContentStyles}>
                      <div css={css`display: flex; align-items: center; padding-left: 8px;`}>
                        <img src={youtube_file} alt="youtube_file" style={{ marginRight: "15px" }} />
                        {url}
                      </div>
                      <button
                        css={replaceFileStyles}
                        onClick={(e) => {
                          e.preventDefault();
                          clearData();
                        }}
                      >
                        <Icon name="refresh" size="md" className="itemWrapper" />
                        <div css={replaceTextStyles}>
                          {t("form_inputFileReplaceLink")}
                        </div>
                      </button>
                    </div>
                  </Container>
                  <MediaPreview  
                    imageUrl={asset?.thumbnail_urls[0] ?? asset?.original_url}
                    originalUrl={asset?.original_url}
                    imgType={asset?.mime_type}
                    t={t}
                  />
                </>
              )}
              <>
                <RateFeedback
                  title={t("feedback_rate_description")}
                  feedbackPosition="Description"
                  id=""
                  entityType="asset_id"
                  entityId={asset?.id || null}
                />
                <div css={titleSection}>
                  <div>{t("form_inputFileDesc")}</div>
                  <div
                    onClick={() => setIsEditDescOpen(true)}
                    css={editBttn}
                  >
                    <Icon name="edit-01" size="xs"/>
                    {t("form_inputFileDescEditBtn")}
                  </div>
                </div>
                <div css={textareaStyles}>{textToUpload}</div>
              </>
            </div>
          )}
        </>
      )}

      {error && (
        <span style={{ color: "#B82223" }}>
          {t("form_inputVideoUrlError")}
        </span>
      )}

      {isEditDescOpen && (
        <EditDescriptionModal
          setIsEditDescOpen={setIsEditDescOpen}
          value={textToUpload}
          characterCount={characterCount}
          setCharacterCount={setCharacterCount}
          setReview={handleTextChange}
          CHARACTER_LIMIT={CHARACTER_LIMIT}
        />
      )}
    </div>
  );
};

export default YoutubeUrlUpload;
